import React from 'react';
import { useTranslation, Trans } from 'react-i18next';

import Modal, { ModalContent, ModalFooter } from 'Components/Modal';
import { useMyCourseData, useOnlineCoursesData } from 'Domain/MyCourseData';
import { useGoToPurchase } from 'Domain/ExternalNavigation';
import { useClassroomNavigation } from 'Util/Hooks';
import {
  sendDataToGTM,
  GTM_EVENT_NAME,
  GTM_BUTTON_LOCATION_NAME,
  GTM_DATA_LAYER_PROPERTY,
} from 'Util/Analytics';

export const PREVIEW_MESSAGE_TYPE = {
  ACKNOWLEDGE: 'ACKNOWLEDGE',
  LOCKED: 'LOCKED',
};

const getMessage = ({
  t,
  deactivateInterruption,
  previewModalType,
  handlePurchaseButtonClicked,
  enablePurchaseUrl,
}) => {
  switch (previewModalType) {
    case PREVIEW_MESSAGE_TYPE.ACKNOWLEDGE: {
      return {
        modalContent: {
          imageUrl: '/assets/images/classroom/previewModal.svg',
          title: (
            <Trans
              defaults={`${t('COURSE_PREVIEW.PREVIEW_MODAL_TITLE')}`}
              components={[<br key="title" />]}
            />
          ),
          description: `${
            enablePurchaseUrl
              ? t('COURSE_PREVIEW.PREVIEW_MODAL_DESCRIPTION')
              : t('COURSE_PREVIEW.PREVIEW_MODAL_PAAS_DESCRIPTION')
          }`,
        },
        modalFooter: {
          justifyContent: 'center',
          primaryButtonLabel: `${t('BUTTON.ACKNOWLEDGE')}`,
          onPrimaryButtonClick: deactivateInterruption,
        },
      };
    }
    case PREVIEW_MESSAGE_TYPE.LOCKED:
    default: {
      return {
        modalContent: {
          imageUrl: '/assets/images/classroom/previewModal.svg',
          title: `${t('COURSE_PREVIEW.PREVIEW_LOCKED_MODAL_TITLE')}`,
          description: `${t('COURSE_PREVIEW.PREVIEW_LOCKED_MODAL_DESCRIPTION')}`,
        },
        modalFooter: {
          justifyContent: 'center',
          onPrimaryButtonClick: handlePurchaseButtonClicked,
          onSecondaryButtonClick: deactivateInterruption,
          primaryButtonLabel: enablePurchaseUrl && `${t('BUTTON.PURCHASE_COURSE')}`,
          secondaryButtonLabel: `${t('BUTTON.LATER')}`,
        },
      };
    }
  }
};

const PreviewModal = ({ previewModalType, isInterrupting, deactivateInterruption, ...props }) => {
  const { t } = useTranslation('classroom');
  const { data: myCourseData, courseCode } = useMyCourseData();
  const { data: onlineCourseData } = useOnlineCoursesData({ courseCode: courseCode });
  const { currentSection, currentSubsection } = useClassroomNavigation();
  const { enablePurchaseUrl, purchaseUrl } = useGoToPurchase(onlineCourseData?.SKUCode, {
    utmMedium: 'Purchase - Modal Locked',
    courseTitle: myCourseData.courseInfo?.title,
    sectionTitle: currentSection.title,
    subsectionTitle: currentSubsection.title,
  });

  const handlePurchaseButtonClicked = () => {
    window.location = purchaseUrl;

    // logging GTM event
    sendDataToGTM({
      event: GTM_EVENT_NAME.TRIAL_PURCHASE_CLICK,
      [GTM_DATA_LAYER_PROPERTY.PRODUCT_TITLE]: onlineCourseData?.title,
      [GTM_DATA_LAYER_PROPERTY.BUTTON_LOCATION]: GTM_BUTTON_LOCATION_NAME.MODAL,
    });
  };

  const { modalContent, modalFooter } = getMessage({
    t,
    deactivateInterruption,
    previewModalType,
    handlePurchaseButtonClicked,
    enablePurchaseUrl,
  });

  return (
    <Modal
      contents={<ModalContent {...modalContent} />}
      footer={<ModalFooter {...modalFooter} />}
      footerProps={{ mt: 7 }}
      muiProps={{ isActive: isInterrupting }}
      {...props}
    />
  );
};

export default PreviewModal;
